import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import CTA from "../components/cta";
import "../components/faq.css";
import "./villas-for-sale-abu-dhabi.css";

const keyBenefits = [
  { icon: "fas fa-tag", title: "Competitive launch prices" },
  { icon: "fas fa-file-invoice-dollar", title: "Flexible and investor-friendly payment plans" },
  { icon: "fas fa-city", title: "Access to new, modern developments" },
  { icon: "fas fa-chart-line", title: "High capital appreciation potential" },
  { icon: "fas fa-coins", title: "Attractive rental yields upon completion" },
];

const finderBenefits = [
  { icon: "fas fa-list-check", title: "Personalized property recommendations" },
  { icon: "fas fa-rocket", title: "Early access to new project launches" },
  { icon: "fas fa-chart-pie", title: "Detailed market analysis and ROI insights" },
  { icon: "fas fa-handshake", title: "End-to-end support from selection to booking" },
];

const faqs = [
  {
    icon: "fas fa-file-signature",
    question: "What are off-plan properties in Abu Dhabi?",
    answer: "Off-plan properties are real estate projects that are purchased directly from developers before completion, often at lower prices with flexible payment plans.",
  },
  {
    icon: "fas fa-chart-line",
    question: "Is investing in off-plan properties in Abu Dhabi a good decision?",
    answer: "Yes, it offers strong potential for capital appreciation, lower entry costs, and attractive payment structures, making it ideal for both investors and end-users.",
  },
  {
    icon: "fas fa-handshake",
    question: "How does NV Real Estate help me find the right property?",
    answer: "We act as your trusted property finder in Abu Dhabi, providing personalized recommendations, exclusive project access, and complete support from selection to booking.",
  },
  {
    icon: "fas fa-coins",
    question: "Are payment plans available for off-plan properties?",
    answer: "Most developers offer flexible installment plans, allowing you to pay over the construction period and sometimes even after handover.",
  },
  {
    icon: "fas fa-triangle-exclamation",
    question: "What are the risks of buying off-plan property?",
    answer: "The most common risk is project delay. Nova Vision Real Estate minimizes this by recommending only verified and trusted developments.",
  },
  {
    icon: "fas fa-rotate",
    question: "Can I resell an off-plan property before completion?",
    answer: "Yes. It depends on developer policies and payment milestones achieved.",
  },
];

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  mainEntity: faqs.map(({ question, answer }) => ({
    "@type": "Question",
    name: question,
    acceptedAnswer: { "@type": "Answer", text: answer },
  })),
};

const breadcrumbSchema = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  itemListElement: [
    { "@type": "ListItem", position: 1, name: "Home", item: "https://nvrealestate.ae/" },
    { "@type": "ListItem", position: 2, name: "Off Plan Properties in Abu Dhabi", item: "https://nvrealestate.ae/off-plan-properties-abu-dhabi" },
  ],
};

function OffPlanPropertiesAbuDhabi() {
  const [activeFaq, setActiveFaq] = useState(0);

  return (
    <>
      <Helmet>
        <title>Off Plan Properties in Abu Dhabi – New Developments</title>
        <meta
          name="description"
          content="Searching for the best off-plan properties in Abu Dhabi to buy the best one for your needs? NV Real Estate shows you the best available properties matching your needs and help you buy the right one."
        />
        <meta name="robots" content="index, follow, max-image-preview:large" />
        <script type="application/ld+json">{JSON.stringify(faqSchema)}</script>
        <script type="application/ld+json">{JSON.stringify(breadcrumbSchema)}</script>
      </Helmet>

      <main
        className="villa-page"
        style={{ "--villa-section-image": `url(${process.env.PUBLIC_URL}/assets/images/abu.jpg)` }}
      >
        <section className="villa-hero" aria-labelledby="villa-page-title">
          <div
            className="villa-hero__image"
            aria-hidden="true"
            style={{ backgroundImage: `url("/images/seo/off-plan-properties-abu-dhabi/01.png")` }}
          />
          <div className="villa-hero__overlay" aria-hidden="true" />
          <div className="container villa-hero__inner">
            <div className="villa-hero__content">
              <span className="villa-eyebrow">Abu Dhabi investment specialists</span>
              <h1 id="villa-page-title">Off-Plan Properties Abu Dhabi – Smart Investment Starts Here</h1>
              <div className="villa-hero__actions">
                <Link className="villa-button villa-button--primary" to="/property-search-result?offering_type=Sale&city=&developer=&developer_name=&project=&project_name=&property_type=&bedrooms=&bathrooms=&minArea=&maxArea=&minPrice=&maxPrice=">
                  Explore off-plan projects
                </Link>
                <a className="villa-button villa-button--ghost" href="tel:+971501577822">
                  Call +971 50 157 7822
                </a>
              </div>
            </div>
          </div>
        </section>

        <section className="villa-intro villa-section">
          <div className="container villa-intro__grid">
            <div className="villa-intro__image">
              <img
                src="/images/seo/off-plan-properties-abu-dhabi/02.png"
                alt="Off-plan properties in Abu Dhabi"
              />
              <div className="villa-intro__note">
                <strong>Invest early</strong>
                <span>Flexible instalments as the project progresses</span>
              </div>
            </div>
            <div className="villa-section-heading villa-intro__content">
              <span className="villa-kicker">Nova Vision Real Estate</span>
              <h2>Off-Plan Properties Abu Dhabi – Smart Investment Starts Here</h2>
              <p>
                Nova Vision Real Estate is the most reliable place for you in Abu Dhabi if you are
                looking for a place to own a property in Abu Dhabi with low investments. We bring
                you an opportunity for you to invest in some of the most in-demand off-plan
                properties in Abu Dhabi, which enables you to invest early and reap high returns in
                the long term. The best aspect of this investment is that you can become part of a
                project with a small amount, and you have to pay the rest of the amount in
                instalments as the project progresses. Hence, you invest early and reap high returns
                in the long run. So, don't think much and give us a call now to know about the best
                investment options for you.
              </p>
            </div>
          </div>
        </section>

        <section className="villa-benefits villa-benefits--centered-cards villa-section" aria-labelledby="why-offplan-heading">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">Why off-plan</span>
              <h2 id="why-offplan-heading">Why Choose Off-Plan Properties in Abu Dhabi?</h2>
              <p>
                Investments in off-plan properties are best suited to those seeking affordable,
                flexible, and appreciating investment options. Due to the low cost and flexible
                payment structure, one can invest in quality properties without the pressure.
                Moreover, the value of the property increases as the project advances, making it a
                profitable investment.
              </p>
              <p><strong>Key benefits include:</strong></p>
            </div>
            <div className="villa-benefits__grid">
              {keyBenefits.map((item, index) => (
                <article className="villa-benefit-card" key={item.title}>
                  <span className="villa-benefit-card__number" aria-hidden="true">0{index + 1}</span>
                  <span className="villa-benefit-card__icon" aria-hidden="true"><i className={item.icon} /></span>
                  <h3>{item.title}</h3>
                </article>
              ))}
            </div>
            <p className="villa-process__closing">
              Abu Dhabi's real estate market continues to grow, supported by government initiatives,
              infrastructure development, and increasing investor confidence, making off-plan
              properties a strategic choice.
            </p>
          </div>
        </section>

        <section className="villa-benefits villa-section" aria-labelledby="finder-heading">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">Property finder</span>
              <h2 id="finder-heading">Simplified Property Finder in Abu Dhabi</h2>
              <p>
                Finding the right property requires expertise and market insight. NV Real Estate
                acts as your trusted property finder in Abu Dhabi, ensuring you invest in projects
                that align with your financial goals and lifestyle needs.
              </p>
              <p>
                Our process is tailored and transparent. We analyze your requirements, budget, and
                investment strategy, then recommend projects that offer the best value. With access
                to exclusive launches and developer partnerships, we help you secure opportunities
                that are not always available in the open market.
              </p>
              <p><strong>You benefit from:</strong></p>
            </div>
            <div className="villa-benefits__grid">
              {finderBenefits.map((item, index) => (
                <article className="villa-benefit-card" key={item.title}>
                  <span className="villa-benefit-card__number" aria-hidden="true">0{index + 1}</span>
                  <span className="villa-benefit-card__icon" aria-hidden="true"><i className={item.icon} /></span>
                  <h3>{item.title}</h3>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section className="villa-advisor villa-section">
          <div className="container">
            <div className="villa-advisor__card">
              <div>
                <span className="villa-kicker">Why Nova Vision</span>
                <h2>Why Trust Nova Vision Real Estate?</h2>
                <p>
                  Choosing the right real estate partner is essential when investing in off-plan
                  properties in Abu Dhabi. NV Real Estate is recognized for its professionalism,
                  market expertise, and client-first approach.
                </p>
                <p>Here's why clients choose us:</p>
                <a className="villa-button villa-button--light" href="https://wa.me/971501577822" target="_blank" rel="noreferrer">
                  Chat on WhatsApp
                </a>
                <p className="villa-advisor__closing">
                  We focus on delivering value, not just transactions. Our goal is to ensure your
                  investment is secure, profitable, and aligned with your long-term objectives.
                </p>
              </div>
              <ul>
                <li>Strong relationships with top Abu Dhabi developers</li>
                <li>Verified and high-potential property listings</li>
                <li>Transparent communication and honest advice</li>
                <li>Experienced team with deep local market knowledge</li>
                <li>Complete assistance from booking to handover</li>
              </ul>
            </div>
          </div>
        </section>

        <section className="villa-benefits villa-section" aria-labelledby="start-journey-heading">
          <div className="container">
            <div className="villa-section-heading villa-section-heading--center">
              <span className="villa-kicker">Get started</span>
              <h2 id="start-journey-heading">Start Your Investment Journey Today!</h2>
            </div>
          </div>
        </section>

        <section className="nv-faq-section villa-faq" aria-labelledby="villa-faq-title">
          <div className="nv-faq-container">
            <header className="nv-faq-heading">
              <span className="nv-faq-label">Investor questions</span>
              <h2 id="villa-faq-title">Frequently Asked Questions</h2>
              <p>Practical answers to help you begin your off-plan investment with confidence.</p>
            </header>

            <div className="villa-faq__main">
              <div className="nv-faq-accordion">
                {faqs.map((faq, index) => {
                  const isActive = activeFaq === index;
                  return (
                    <article className={`nv-faq-item${isActive ? " active" : ""}`} key={faq.question}>
                      <button
                        className="nv-faq-question"
                        type="button"
                        onClick={() => setActiveFaq((current) => (current === index ? null : index))}
                        aria-expanded={isActive}
                        aria-controls={`offplan-faq-answer-${index}`}
                      >
                        <span className="nv-faq-question-icon"><i className={faq.icon} /></span>
                        <strong>{faq.question}</strong>
                        <span className="nv-faq-toggle" aria-hidden="true" />
                      </button>
                      <div
                        className="nv-faq-answer"
                        id={`offplan-faq-answer-${index}`}
                        aria-hidden={!isActive}
                      >
                        <p>{faq.answer}</p>
                      </div>
                    </article>
                  );
                })}
              </div>
            </div>

            <div className="nv-faq-trust-strip">
              <div className="nv-faq-trust-item"><i className="fas fa-city" /><div><strong>Off-Plan Access</strong><span>Early Project Launches</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-map-marker-alt" /><div><strong>Local Knowledge</strong><span>Across Abu Dhabi</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-file-signature" /><div><strong>Booking Support</strong><span>Selection to Handover</span></div></div>
              <div className="nv-faq-trust-item"><i className="fas fa-headset" /><div><strong>Dedicated Advisor</strong><span>One Point of Contact</span></div></div>
            </div>
            <p className="villa-faq__contact">
              Call us now at <a href="tel:+971501577822">+971-501577822</a>. You can also email us
              at <a href="mailto:info@nvrealestate.ae">info@nvrealestate.ae</a>.
            </p>
          </div>
        </section>
      </main>

      <CTA />

      <style>{`
        .villa-benefits--centered-cards .villa-benefits__grid {
          display: flex;
          flex-wrap: wrap;
          justify-content: center;
          gap: 24px;
        }
        .villa-benefits--centered-cards .villa-benefit-card {
          flex: 1 1 260px;
          max-width: 300px;
        }
        @media (max-width: 700px) {
          .villa-benefits--centered-cards .villa-benefit-card {
            flex: 1 1 100%;
            max-width: 420px;
          }
        }
      `}</style>
    </>
  );
}

export default OffPlanPropertiesAbuDhabi;